import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { isIPhone } from 'lib/iphone';

interface TableCenterTimerProps {
  seconds: number;
  maxSeconds?: number;
  label?: string;
  variant?: 'turn' | 'countdown';
  compact?: boolean;
  className?: string;
}

/** Prominent timer in the middle of the felt — conic progress ring + smooth RAF countdown */
export default function TableCenterTimer({
  seconds,
  maxSeconds,
  label,
  variant = 'turn',
  compact = false,
  className = '',
}: TableCenterTimerProps) {
  const isCountdown = variant === 'countdown';
  const safeMax = Math.max(1, maxSeconds ?? seconds);
  const urgentThreshold = isCountdown ? 3 : 5;

  const [displayLeft, setDisplayLeft] = useState(Math.max(0, seconds));
  const anchorRef = useRef<{ at: number; left: number } | null>(null);
  const lastPropRef = useRef(seconds);
  const lastMaxRef = useRef(safeMax);

  useEffect(() => {
    if (lastMaxRef.current === safeMax) return;
    lastMaxRef.current = safeMax;
    const next = Math.max(0, seconds);
    if (next <= 0) return;
    anchorRef.current = { at: Date.now(), left: next };
    setDisplayLeft(next);
  }, [safeMax, seconds]);

  useEffect(() => {
    const next = Math.max(0, seconds);
    const prev = lastPropRef.current;
    lastPropRef.current = next;

    if (next <= 0) {
      anchorRef.current = null;
      setDisplayLeft(0);
      return;
    }

    const isNewCycle = anchorRef.current == null || next > prev + 0.5;
    if (isNewCycle || anchorRef.current == null) {
      anchorRef.current = { at: Date.now(), left: next };
      setDisplayLeft(next);
      return;
    }

    const elapsed = (Date.now() - anchorRef.current.at) / 1000;
    const predicted = Math.max(0, anchorRef.current.left - elapsed);
    if (Math.abs(predicted - next) > 0.35) {
      anchorRef.current = { at: Date.now(), left: next };
      setDisplayLeft(next);
    }
  }, [seconds]);

  useEffect(() => {
    let raf = 0;

    const tick = () => {
      raf = requestAnimationFrame(tick);
      const anchor = anchorRef.current;
      if (!anchor) return;
      const elapsed = (Date.now() - anchor.at) / 1000;
      const left = Math.max(0, anchor.left - elapsed);
      setDisplayLeft(left);
      if (left <= 0) {
        anchorRef.current = null;
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const display = Math.max(0, Math.ceil(displayLeft));
  const progress = Math.max(0, Math.min(1, displayLeft / safeMax));
  const urgent = display <= urgentThreshold && display > 0;
  const critical = display <= (isCountdown ? 2 : 3);
  const progressColor = urgent ? '#ef4444' : '#E8C547';
  const trackColor = isCountdown ? 'rgba(212,175,55,0.22)' : 'rgba(255,255,255,0.12)';
  const hostSizeClass =
    compact && isCountdown
      ? 'tp-center-timer-ring-host-compact'
      : compact
        ? 'tp-center-timer-ring-host-mobile'
        : '';
  const iphoneSvgRing = isIPhone();
  const svgStroke = compact ? 9 : 7;
  const svgRadius = (100 - svgStroke * 2.4) / 2;
  const svgCircumference = 2 * Math.PI * svgRadius;
  const svgOffset = svgCircumference * (1 - progress);

  return (
    <div
      className={`tp-center-timer flex flex-col items-center gap-0.5 ${compact ? 'tp-center-timer-compact' : ''} ${urgent ? 'tp-center-timer-urgent' : ''} ${critical ? 'tp-center-timer-critical' : ''} ${className}`}
    >
      {label && (
        <span
          className={`tp-center-timer-label font-display font-bold uppercase tracking-[0.28em] text-white/70 ${
            compact ? 'text-[7px]' : 'text-[9px] sm:text-[10px]'
          }`}
        >
          {label}
        </span>
      )}
      <div
        className={`tp-center-timer-ring-host relative ${hostSizeClass}${iphoneSvgRing ? ' tp-center-timer-ring-host-svg' : ''}`}
        style={
          {
            '--tp-timer-progress': progress,
            '--tp-timer-progress-color': progressColor,
            '--tp-timer-track-color': trackColor,
          } as CSSProperties
        }
      >
        {iphoneSvgRing ? (
          <svg
            className="tp-center-timer-svg-ring"
            viewBox="0 0 100 100"
            aria-hidden
          >
            <circle
              cx="50"
              cy="50"
              r={svgRadius - svgStroke * 0.55}
              fill="rgba(0, 0, 0, 0.58)"
            />
            <circle
              cx="50"
              cy="50"
              r={svgRadius}
              fill="none"
              stroke={trackColor}
              strokeWidth={svgStroke}
            />
            <circle
              cx="50"
              cy="50"
              r={svgRadius}
              fill="none"
              stroke={progressColor}
              strokeWidth={svgStroke}
              strokeLinecap="round"
              strokeDasharray={svgCircumference}
              strokeDashoffset={svgOffset}
              transform="rotate(-90 50 50)"
              className="tp-center-timer-svg-progress"
            />
          </svg>
        ) : (
          <>
            <div className="tp-center-timer-conic-ring" aria-hidden />
            <div className="tp-center-timer-conic-inner" aria-hidden />
          </>
        )}
        <span
          className={`tp-center-timer-digit absolute inset-0 flex items-center justify-center font-display font-bold tabular-nums ${
            compact
              ? urgent
                ? 'text-sm text-red-300'
                : 'text-sm text-tp-gold'
              : urgent
                ? 'text-2xl text-red-300'
                : 'text-2xl text-tp-gold sm:text-3xl'
          }`}
        >
          {display}
        </span>
      </div>
    </div>
  );
}
